<template>
  <div class="view-only-banner">
    <v-icon color="white">mdi-eye</v-icon>
    <span class="view-only-banner__text">View only mode</span>

    <template v-if="isPublicWorkflow">
      <template v-if="isUserLoggedIn">
        <v-menu v-model="menuOpen" location="bottom" :close-on-content-click="true">
          <template v-slot:activator="{ props }">
            <span v-bind="props" class="view-only-banner__link">Clone this workflow</span>
          </template>
          <v-list density="compact">
            <v-list-subheader>Clone to project:</v-list-subheader>
            <v-list-item
              v-for="(projectMembership, index) in projectMemberships"
              :key="index"
              :disabled="isCloning"
              @click="cloneToProject(projectMembership.project.id)"
              prepend-icon="mdi-folder-outline"
            >
              <v-list-item-title>{{ projectMembership.project?.name }}</v-list-item-title>
            </v-list-item>
            <v-divider class="my-1" />
            <v-list-item
              :disabled="isCloning"
              @click="openNewProjectDialog"
              prepend-icon="mdi-plus"
            >
              <v-list-item-title>New project</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
        <settings-project-dialog
          ref="projectDialog"
          :set-project="false"
          @project-created="onProjectCreated"
        >
          <template #activator><span /></template>
        </settings-project-dialog>
      </template>

      <template v-else>
        <router-link :to="loginRedirectRoute" class="view-only-banner__link">Log in to clone this workflow</router-link>
      </template>
    </template>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { EventBus } from '@/event-bus/event-bus.js'
import SettingsProjectDialog from '@/components/SettingsProjectDialog.vue'

const store = useStore()
const router = useRouter()

const isUserLoggedIn = computed(() => store.state.isUserLoggedIn)
const projectMemberships = computed(() => store.state.projectMemberships.projectMemberships)
const workflow = computed(() => store.state.workflow.workflow)
const isPublicWorkflow = computed(() => workflow.value?.accessLevel === 'READ')
const loginRedirectRoute = computed(() => ({
  path: '/login',
  query: { redirect: `/workflow/${workflow.value?.projectId}/${workflow.value?.id}` }
}))

const menuOpen = ref(false)
const isCloning = ref(false)
const projectDialog = ref(null)

const openNewProjectDialog = () => {
  projectDialog.value.openProjectDialog()
}

const onProjectCreated = async (projectId) => {
  if (projectId) {
    await cloneToProject(projectId, true)
  }
}

const cloneToProject = async (targetProjectId, isNewProject = false) => {
  isCloning.value = true
  const progressMessage = isNewProject
    ? 'Creating project and cloning workflow...'
    : 'Cloning workflow...'
  store.commit('snackbar/showMessage', { content: progressMessage, showProgress: true, timeout: -1 })
  store.commit('loading/setLoadingMessage', progressMessage)
  try {
    const clonedWorkflow = await store.dispatch('workflows/cloneWorkflow', {
      targetProjectId,
      workflowId: workflow.value.id,
      silent: true
    })
    menuOpen.value = false
    if (clonedWorkflow) {
      store.commit('snackbar/showMessage', { content: 'Cloning workflow completed successfully.' })
      await router.push(`/workflow/${targetProjectId}/${clonedWorkflow.id}`)
      EventBus.$emit('get-workflow', clonedWorkflow.id)
    }
  } finally {
    isCloning.value = false
  }
}
</script>

<style>
.view-only-banner {
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  height: 39px;
  background: #4C83D1;
}

.view-only-banner__text {
  padding: 0 10px;
}

.view-only-banner__link {
  color: white;
  text-decoration: underline;
  cursor: pointer;
  margin-left: 10px;
  font-weight: 500;
}

.view-only-banner__link:hover {
  opacity: 0.85;
}
</style>
